import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { Accordion, AccordionDetails, AccordionSummary, Box, CircularProgress, Typography } from "@mui/material"
import React, { useCallback, useContext, useEffect } from "react"
import { useParams } from "react-router-dom"
import { customPalette } from "../../../../../../../../../theme"
import { SecondaryButton } from "../../../../../../../../components/buttons/SecondaryButton/SecondaryButton"
import { IconInbox } from "../../../../../../../../components/custom-icons/icon-inbox"
import { TabEnum } from "../../../../../../../../components/tabs/tabs"
import { pagesUrl } from "../../../../../../../../core/appConstants"
import { BSCategoriesContext } from "../../../../../../../../core/context/beem-shot/BSCategory/BSCategoriesContext"
import { BSItemContext } from "../../../../../../../../core/context/beem-shot/BSItems/BSItemContext"
import { BSVariantContext } from "../../../../../../../../core/context/beem-shot/BSVariant/BSVariantContext"
import { AppNavBarContext, NavbarTypeEnum } from "../../../../../../../../core/context/nav-bar/AppNavBarContext"
import { CoverImgProjectContext } from "../../../../../../../../core/context/project/CoverImgProjectContext"
import { resolveUrl } from "../../../../../../../../core/services/http-service"
import { useAccordion } from "../../../building/BSCalculationPage/BSCalculationForm/components/AdvancedQuestionnaire/useAccordion"
import { BSBuildingContext } from "../../../building/components/context/BSBuildingContext"
import { BSMorphologyContext } from "../BSBuildingMorphologyTab/context/BSMorphologyContext"
import { BSItemList } from "./components/BSItemList/BSItemList"
import { BSConfirmResetDialogContextProvider } from "./components/dialog/BSConfirmResetDialog/BSConfirmResetDialogContext"
import { BSUpdateMaterialResultDialogContextProvider } from "./components/dialog/BSCustomMaterialDialog/BSUpdateMaterialResultDialogContext"
import { BSConfirmResetAllDialogContext } from "./components/dialog/BSCustomResetAllDialog/BSConfirmResetAllDialogContext"
import { BSDeleteCustomDialogContextProvider } from "./components/dialog/BSDeleteCustomDialog/BSDeleteCustomDialogContext"

/**
 * This is component is still used in Beem Morpho
 */

export function BSCustomizationPage(): React.JSX.Element {
  const { projectId, bsVariantId } = useParams()

  const { expanded: categoryExpanded, handleExpanded: handleExpandedCategory } = useAccordion()
  const { expanded: subcategoryExpanded, handleExpanded: handleSubCategoryExpanded } = useAccordion()

  const { bsCategories } = useContext(BSCategoriesContext)
  const { subCategoriesForCategory } = useContext(BSItemContext)
  const { openConfirmResetAllDialog } = useContext(BSConfirmResetAllDialogContext)
  const { setTypeNavBar, setImageFile, setIcon, setTitle, setPreviousUrl } = useContext(AppNavBarContext)
  const { file } = useContext(CoverImgProjectContext)
  const { selectedVariant } = useContext(BSVariantContext)
  const { selectedBSBuilding } = useContext(BSBuildingContext)
  const { isEstimating } = useContext(BSMorphologyContext)

  useEffect(() => {
    setTypeNavBar(NavbarTypeEnum.BUILDING_CALCUL)
    setPreviousUrl(
      resolveUrl(pagesUrl.VARIANT_DETAILS, [projectId, bsVariantId], {
        bsBuildingId: selectedBSBuilding?.id,
        tab: TabEnum.BUILDING_DASHBOARD,
      })
    )
    setImageFile(file)
    setIcon(<IconInbox sx={{ color: customPalette.black, fontWeight: "2px" }} />)
    setTitle(selectedVariant?.name ?? "")
  }, [
    projectId,
    bsVariantId,
    setPreviousUrl,
    setTypeNavBar,
    setImageFile,
    file,
    setIcon,
    setTitle,
    selectedVariant?.name,
    selectedBSBuilding?.id,
  ])

  const handleOpenReset: (event: React.MouseEvent<HTMLButtonElement>) => void = useCallback(
    (event) => {
      event.stopPropagation()
      openConfirmResetAllDialog()
    },
    [openConfirmResetAllDialog]
  )

  return (
    <Box display="flex" flexDirection="column" gap={1} pt={2}>
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        <Typography variant="h6">Personnalisez vos calculs</Typography>
        <SecondaryButton onClick={handleOpenReset} label="Réinitialiser" />
      </Box>

      {isEstimating ? (
        <Box
          sx={{ height: "50vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
          <CircularProgress />
          <Typography>Rafraîchissement des fiches en cours...</Typography>
        </Box>
      ) : (
        <BSUpdateMaterialResultDialogContextProvider>
          <BSConfirmResetDialogContextProvider>
            <BSDeleteCustomDialogContextProvider>
              {bsCategories
                .filter((bsCategory) => Object.keys(subCategoriesForCategory).includes(bsCategory.name))
                .map((bsCategory) => (
                  <Accordion
                    id={bsCategory.name}
                    key={bsCategory.name}
                    expanded={categoryExpanded === bsCategory.name}
                    onChange={() => {
                      handleSubCategoryExpanded("")
                      handleExpandedCategory(bsCategory.name)
                    }}
                    disableGutters
                    square
                    sx={{
                      borderRadius: 6,
                      minWidth: "100%",
                    }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>{bsCategory.label}</AccordionSummary>
                    <AccordionDetails>
                      {bsCategory?.subCategories
                        ?.filter((subCategory) =>
                          subCategoriesForCategory[bsCategory.name]?.some((sc) => sc.name === subCategory.name)
                        )
                        .map((subCategory) => (
                          <Accordion
                            id={subCategory.name}
                            key={subCategory.name}
                            expanded={subcategoryExpanded === subCategory.name}
                            onChange={() => {
                              handleSubCategoryExpanded(subCategory.name)
                            }}
                            elevation={0}
                            square
                            disableGutters
                            slotProps={{
                              transition: {
                                unmountOnExit: true,
                              },
                            }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>{subCategory.label}</AccordionSummary>
                            <AccordionDetails>
                              <BSItemList subCategory={subCategory} />
                            </AccordionDetails>
                          </Accordion>
                        ))}
                    </AccordionDetails>
                  </Accordion>
                ))}
            </BSDeleteCustomDialogContextProvider>
          </BSConfirmResetDialogContextProvider>
        </BSUpdateMaterialResultDialogContextProvider>
      )}
    </Box>
  )
}
